/**
 * Timesheet Header Component
 * Navigation, filters, and controls for timesheet views
 */

import React from 'react';
import { Button, DatePicker, Space, Switch, Tooltip } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { TimesheetView } from '@/api/types/timesheet.types';
import './TimesheetHeader.css';

dayjs.extend(isoWeek);

interface TimesheetHeaderProps {
  view: TimesheetView;
  onViewChange: (view: TimesheetView) => void;
  year: number;
  month: number;
  week?: number;
  date?: string;
  onPeriodChange: (year: number, month: number) => void;
  onWeekChange?: (year: number, week: number) => void;
  onDateChange?: (date: string) => void;
  onYearChange?: (year: number) => void;
  projectId?: string;
  onProjectChange?: (projectId?: string) => void;
  showAll: boolean;
  onShowAllChange: (showAll: boolean) => void;
  onExport?: () => void;
  onRefresh?: () => void;
}

export const TimesheetHeader: React.FC<TimesheetHeaderProps> = ({
  view,
  onViewChange,
  year,
  month,
  week = 1,
  date = dayjs().format('YYYY-MM-DD'),
  onPeriodChange,
  onWeekChange,
  onDateChange,
  onYearChange,
  showAll,
  onShowAllChange,
  onExport,
  onRefresh,
}) => {
  // Navigation handlers based on view
  const handlePrevious = () => {
    switch (view) {
      case 'daily':
        if (onDateChange) {
          const prev = dayjs(date).subtract(1, 'day');
          onDateChange(prev.format('YYYY-MM-DD'));
        }
        break;
      case 'weekly': {
        if (onWeekChange) {
          const current = dayjs().year(year).isoWeek(week);
          const prev = current.subtract(1, 'week');
          onWeekChange(prev.year(), prev.isoWeek());
        }
        break;
      }
      case 'monthly': {
        const prevMonth = dayjs(new Date(year, month - 1, 1)).subtract(
          1,
          'month'
        );
        onPeriodChange(prevMonth.year(), prevMonth.month() + 1);
        break;
      }
      case 'yearly':
        if (onYearChange) {
          onYearChange(year - 1);
        }
        break;
    }
  };

  const handleNext = () => {
    switch (view) {
      case 'daily':
        if (onDateChange) {
          const next = dayjs(date).add(1, 'day');
          onDateChange(next.format('YYYY-MM-DD'));
        }
        break;
      case 'weekly': {
        if (onWeekChange) {
          const current = dayjs().year(year).isoWeek(week);
          const next = current.add(1, 'week');
          onWeekChange(next.year(), next.isoWeek());
        }
        break;
      }
      case 'monthly': {
        const nextMonth = dayjs(new Date(year, month - 1, 1)).add(1, 'month');
        onPeriodChange(nextMonth.year(), nextMonth.month() + 1);
        break;
      }
      case 'yearly':
        if (onYearChange) {
          onYearChange(year + 1);
        }
        break;
    }
  };

  const handleToday = () => {
    const today = dayjs();
    switch (view) {
      case 'daily':
        if (onDateChange) {
          onDateChange(today.format('YYYY-MM-DD'));
        }
        break;
      case 'weekly':
        if (onWeekChange) {
          onWeekChange(today.year(), today.isoWeek());
        }
        break;
      case 'monthly':
        onPeriodChange(today.year(), today.month() + 1);
        break;
      case 'yearly':
        if (onYearChange) {
          onYearChange(today.year());
        }
        break;
    }
  };

  // Date picker change handlers
  const handleDatePickerChange = (pickedDate: Dayjs | null) => {
    if (!pickedDate) return;

    switch (view) {
      case 'daily':
        if (onDateChange) {
          onDateChange(pickedDate.format('YYYY-MM-DD'));
        }
        break;
      case 'weekly':
        if (onWeekChange) {
          onWeekChange(pickedDate.year(), pickedDate.isoWeek());
        }
        break;
      case 'monthly':
        onPeriodChange(pickedDate.year(), pickedDate.month() + 1);
        break;
      case 'yearly':
        if (onYearChange) {
          onYearChange(pickedDate.year());
        }
        break;
    }
  };

  // Get current date object based on view
  const getCurrentDate = () => {
    switch (view) {
      case 'daily':
        return dayjs(date);
      case 'weekly':
        return dayjs().year(year).isoWeek(week);
      case 'monthly':
        return dayjs(new Date(year, month - 1, 1));
      case 'yearly':
        return dayjs().year(year);
      default:
        return dayjs();
    }
  };

  // Get picker type based on view
  const getPickerType = (): 'date' | 'week' | 'month' | 'year' => {
    switch (view) {
      case 'daily':
        return 'date';
      case 'weekly':
        return 'week';
      case 'monthly':
        return 'month';
      case 'yearly':
        return 'year';
      default:
        return 'month';
    }
  };

  // Get format based on view
  const getFormat = () => {
    switch (view) {
      case 'daily':
        return 'MMMM D, YYYY';
      case 'weekly':
        return 'YYYY-[W]WW';
      case 'monthly':
        return 'MMMM YYYY';
      case 'yearly':
        return 'YYYY';
      default:
        return 'MMMM YYYY';
    }
  };

  return (
    <div className="timesheet-header">
      <div className="timesheet-header-main">
        {/* View Selector */}
        <div className="timesheet-view-selector">
          <Button.Group>
            <Button
              type={view === 'daily' ? 'primary' : 'default'}
              onClick={() => onViewChange('daily')}
            >
              Daily
            </Button>
            <Button
              type={view === 'weekly' ? 'primary' : 'default'}
              onClick={() => onViewChange('weekly')}
            >
              Weekly
            </Button>
            <Button
              type={view === 'monthly' ? 'primary' : 'default'}
              onClick={() => onViewChange('monthly')}
            >
              Monthly
            </Button>
            <Button
              type={view === 'yearly' ? 'primary' : 'default'}
              onClick={() => onViewChange('yearly')}
            >
              Yearly
            </Button>
          </Button.Group>
        </div>

        {/* Period Navigation */}
        <div className="timesheet-period-nav">
          <Space>
            <Button
              icon={<LeftOutlined />}
              onClick={handlePrevious}
              title={`Previous ${view}`}
            />
            <DatePicker
              picker={getPickerType()}
              value={getCurrentDate()}
              onChange={handleDatePickerChange}
              format={getFormat()}
              allowClear={false}
            />
            <Button
              icon={<RightOutlined />}
              onClick={handleNext}
              title={`Next ${view}`}
            />
            <Button onClick={handleToday}>Today</Button>
          </Space>
        </div>
      </div>

      <div className="timesheet-header-actions">
        <Space>
          {/* Show All Toggle */}
          <Tooltip
            title={
              showAll
                ? 'Showing all statuses (approved, pending, rejected)'
                : 'Showing only approved timesheets'
            }
          >
            <div className="timesheet-show-all-toggle">
              <Switch
                checked={showAll}
                onChange={onShowAllChange}
                checkedChildren="All"
                unCheckedChildren="Approved"
              />
            </div>
          </Tooltip>

          {/* Refresh */}
          {onRefresh && (
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Refresh
            </Button>
          )}

          {/* Export */}
          {onExport && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onExport}
            >
              Export
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};
